import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEntityTranslationMap } from "@/hooks/use-place-translations";

export type PublicCity = {
  id?: string;
  slug: string;
  name: string;
  region: string | null;
  tagline?: string | null;
  image_url?: string | null;
  accent?: string | null;
  population?: string | null;
  founded?: number | null;
  lat?: number | null;
  lng?: number | null;
};

const CITY_FIELDS = ["name", "tagline", "region"];

export function usePublicCities() {
  const { data } = useQuery({
    queryKey: ["public-cities"],
    queryFn: async (): Promise<PublicCity[]> => {
      const { data, error } = await supabase
        .from("cities")
        .select("id, slug, name, region, tagline, image_url, accent, population, founded, lat, lng, sort_order")
        .eq("active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      if (!data) return [];
      return data.map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
        region: c.region,
        tagline: c.tagline,
        image_url: c.image_url,
        accent: c.accent,
        population: c.population,
        founded: c.founded,
        lat: c.lat,
        lng: c.lng,
      }));
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: [],
  });

  // Overlay stored translations for the active language (no-op in English).
  const translations = useEntityTranslationMap("city", CITY_FIELDS);
  const rows = data ?? [];
  return useMemo(() => {
    if (Object.keys(translations).length === 0) return rows;
    return rows.map((c) => {
      const t = c.id ? translations[c.id] : undefined;
      if (!t) return c;
      return {
        ...c,
        name: t.name?.trim() || c.name,
        tagline: t.tagline?.trim() || c.tagline,
      };
    });
  }, [rows, translations]);
}

import { CITIES } from "@/data/cities";

/**
 * Resolve a city slug into a display record by checking admin-managed DB cities
 * first, then the static CITIES fallback. Returns a safe shape so callers never
 * crash when an admin-added city is selected before its public row is cached.
 */
export function useResolvedCity(slug: string | undefined | null) {
  const cities = usePublicCities();
  const fromDb = slug ? cities.find((c) => c.slug === slug) : undefined;
  const fromStatic = slug ? CITIES.find((c) => c.id === slug) : undefined;
  const name = fromDb?.name ?? fromStatic?.name ?? (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "Finland");
  const lat = fromDb?.lat ?? fromStatic?.lat ?? null;
  const lng = fromDb?.lng ?? fromStatic?.lng ?? null;
  return { slug: slug ?? "", name, lat, lng, fromDb, fromStatic };
}

